import { useQuery } from 'react-query'
import { listThreads } from '@/features/tools/api'

const RECENT_CHATS_LIMIT = 6

/**
 * Hook to get the last 6 chats from history
 *
 * @param enablePolling - If true, polls every 30s to update the history
 * @returns Array of recent chats with basic information to display in the sidebar
 *
 * @example
 * ```tsx
 * const { data: recentChats, isLoading, refetch } = useRecentChats(true)
 *
 * recentChats?.map(chat => (
 *   <Link to={chat.url}>{chat.title}</Link>
 * ))
 * ```
 */
export const useRecentChats = (enablePolling = false) => {
  return useQuery(
    ['recent-chats'],
    async ({ signal }) => {
      const response = await listThreads(RECENT_CHATS_LIMIT, 0, signal)

      // Map to simplified format
      return response.threads.map((thread) => ({
        id: thread._id!,
        title: thread.properties?.title || 'Sin título',
        toolName: thread.tool.title,
        toolId: thread.tool._id,
        url:
          thread.tool.slug === 'general-assistant'
            ? `/assistants?t=${thread._id}`
            : `/tools/${thread.tool.slug}?t=${thread._id}`,
        createdAt: thread.createdAt,
        updatedAt: thread.updatedAt,
      }))
    },
    {
      // Cache for 5 minutes
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      // Polling every 30s when enabled
      refetchInterval: enablePolling ? 30000 : false,
      refetchIntervalInBackground: false,
    },
  )
}
